import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

import {
  DynamicDropDownOption,
  DynamicFieldAction,
  DynamicFormDefinition,
} from '@models/dynamic-form.interface';
import { CalculationEngineService } from '@services/calculation-engine.service';

export interface RuleEffect {
  fieldId: string;
  hidden?: boolean;
  readonly?: boolean;
  required?: boolean;
  value?: unknown;
  /** Result of a "loadOptions" action — replaces the target field's dropdown data */
  dataOptions?: DynamicDropDownOption[];
}

const MAX_PROPAGATION_PASSES = 8;

/**
 * Evaluates field rules and returns a list of effects to apply.
 * Mirrors FluidSys_New RuleEngineService — action types:
 *   show | hide | enable | disable | setValue | calculate | setRequired | loadOptions
 *
 * Rule evaluation runs to a fixed point (bounded by MAX_PROPAGATION_PASSES) so that when a
 * "calculate" action updates a target field's value, and that target field itself has rules,
 * those rules are evaluated too in the same pass — mirroring the server's recursive
 * `EvaluateRulesAsync` call from `HandleCalculationAsync`.
 */
@Injectable({ providedIn: 'root' })
export class RuleEngineService {
  constructor(private readonly calcEngine: CalculationEngineService) {}

  /**
   * Evaluates every rule on every field in the definition against the current form values,
   * propagating chained calculate/setValue effects until the value map stabilizes.
   */
  public evaluateAll(definition: DynamicFormDefinition, formGroup: FormGroup): RuleEffect[] {
    const workingValues = this.buildValueMap(definition, formGroup);
    const effectsByField = new Map<string, RuleEffect>();

    for (let pass = 0; pass < MAX_PROPAGATION_PASSES; pass++) {
      let changed = false;

      for (const field of definition.fields) {
        if (!field.rules?.length) continue;

        const sourceValue = workingValues[field.name] ?? workingValues[field.id];

        for (const rule of field.rules) {
          const conditionMet =
            !rule.condition || this.calcEngine.evaluateCondition(sourceValue, rule.condition);

          const newEffects = conditionMet
            ? this.applyActions(rule.actions, rule.targetFieldId, definition, workingValues, sourceValue)
            : this.revertActions(rule.actions, rule.targetFieldId, definition);

          for (const effect of newEffects) {
            this.mergeEffect(effectsByField, effect);

            if (effect.value !== undefined) {
              const targetField = definition.fields.find((f) => f.id === effect.fieldId);
              if (targetField) {
                const previous = workingValues[targetField.name];
                if (previous !== effect.value) {
                  workingValues[targetField.name] = effect.value;
                  workingValues[targetField.id] = effect.value;
                  changed = true;
                }
              }
            }
          }
        }
      }

      if (!changed) break;
    }

    return Array.from(effectsByField.values());
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  private applyActions(
    actions: DynamicFieldAction[],
    targetFieldId: string,
    definition: DynamicFormDefinition,
    formValues: Record<string, unknown>,
    sourceValue: unknown
  ): RuleEffect[] {
    const effects: RuleEffect[] = [];

    for (const action of actions) {
      const effect = this.resolveAction(action, targetFieldId, definition, formValues, sourceValue);
      if (effect) effects.push(effect);
    }

    return effects;
  }

  private resolveAction(
    action: DynamicFieldAction,
    targetFieldId: string,
    definition: DynamicFormDefinition,
    formValues: Record<string, unknown>,
    sourceValue: unknown
  ): RuleEffect | null {
    switch (action.type?.toLowerCase()) {
      case 'show':
        return { fieldId: targetFieldId, hidden: false };

      case 'hide':
        return { fieldId: targetFieldId, hidden: true };

      case 'enable':
        return { fieldId: targetFieldId, readonly: false };

      case 'disable':
        return { fieldId: targetFieldId, readonly: true };

      case 'setrequired':
        return { fieldId: targetFieldId, required: Boolean(action.value) };

      case 'setvalue':
        return { fieldId: targetFieldId, value: action.value };

      case 'calculate': {
        if (!action.expression) return null;
        const result = this.calcEngine.evaluate(
          action.expression,
          formValues,
          definition.fields
        );
        return result !== null && result !== undefined
          ? { fieldId: targetFieldId, value: result }
          : null;
      }

      case 'loadoptions':
        return this.resolveLoadOptions(targetFieldId, definition, sourceValue);

      default:
        return null;
    }
  }

  /**
   * Mirrors FluidSys_New UpdateDropdownOptionsAsync — looks up the target field's
   * cascadingData keyed by the source field's current value.
   */
  private resolveLoadOptions(
    targetFieldId: string,
    definition: DynamicFormDefinition,
    sourceValue: unknown
  ): RuleEffect | null {
    const targetField = definition.fields.find((f) => f.id === targetFieldId);
    if (!targetField) return null;
    if (targetField.type !== 'dropdown' && targetField.type !== 'cascadingDropdown') return null;

    const key = String(sourceValue ?? '');
    const options = targetField.cascadingData?.[key] ?? [];
    return { fieldId: targetFieldId, dataOptions: options };
  }

  /**
   * Produces revert effects when a condition is no longer met. Each action type is
   * restored to the target field's declared default so effects never get "stuck".
   */
  private revertActions(
    actions: DynamicFieldAction[],
    targetFieldId: string,
    definition: DynamicFormDefinition
  ): RuleEffect[] {
    const targetField = definition.fields.find((f) => f.id === targetFieldId);
    const effects: RuleEffect[] = [];

    for (const action of actions) {
      switch (action.type?.toLowerCase()) {
        case 'show':
        case 'hide':
          effects.push({ fieldId: targetFieldId, hidden: targetField?.isHidden ?? false });
          break;
        case 'enable':
        case 'disable':
          effects.push({ fieldId: targetFieldId, readonly: targetField?.isReadonly ?? false });
          break;
        case 'setrequired':
          effects.push({ fieldId: targetFieldId, required: targetField?.isRequired ?? false });
          break;
        case 'setvalue':
        case 'calculate':
          effects.push({ fieldId: targetFieldId, value: targetField?.defaultValue ?? '' });
          break;
        case 'loadoptions':
          effects.push({ fieldId: targetFieldId, dataOptions: targetField?.data ?? [] });
          break;
      }
    }

    return effects;
  }

  /** Merges a new effect into the accumulator, only overwriting properties that are present */
  private mergeEffect(accumulator: Map<string, RuleEffect>, incoming: RuleEffect): void {
    const existing = accumulator.get(incoming.fieldId);
    accumulator.set(incoming.fieldId, existing ? { ...existing, ...incoming } : { ...incoming });
  }

  /** Builds a name-keyed AND id-keyed value map from the reactive form */
  private buildValueMap(
    definition: DynamicFormDefinition,
    formGroup: FormGroup
  ): Record<string, unknown> {
    const raw = formGroup.getRawValue() as Record<string, unknown>;
    const map: Record<string, unknown> = { ...raw };

    // Add name-keyed entries so expressions can reference fields by their `name` property
    for (const field of definition.fields) {
      const value = raw[field.id];
      if (value !== undefined) {
        map[field.name] = value;
      }
    }

    return map;
  }
}

